import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchTransactionsData, fetchWalletData } from '../services/dataApi';
import PageLoader from '../components/PageLoader';
import SearchBar from '../components/SearchBar';
import TransactionsTable from '../components/TransactionsTable';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useUrlPagination from '../hooks/useUrlPagination';
import { searchInData } from '../utils/searchUtils';
import './Page.css';

const Wallet = () => {
  const [wallet, setWallet] = useState('');
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [transactions, setTransactions] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();

  useEffect(() => {
    const loadWallet = async () => {
      setWalletLoading(true);
      setWalletError(null);

      try {
        const result = await fetchWalletData();
        setWallet(String(result.wallet || '').trim());
      } catch (err) {
        setWalletError(err.message || 'An error occurred while loading wallet');
      } finally {
        setWalletLoading(false);
      }
    };

    loadWallet();
  }, []);

  const loadTransactions = useCallback(async (page = 1) => {
    setTransactionsLoading(true);
    setTransactionsError(null);

    try {
      const result = await fetchTransactionsData(page, itemsPerPage, debouncedSearchTerm);
      setTransactions(result.data || result);
      setPagination(result.pagination);
    } catch (err) {
      setTransactionsError(err.message || 'An error occurred while loading transactions');
    } finally {
      setTransactionsLoading(false);
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    loadTransactions(currentPage);
  }, [currentPage, loadTransactions]);

  const qrUrl = useMemo(() => {
    if (!wallet) {
      return '';
    }

    return `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(wallet)}`;
  }, [wallet]);

  const handleCopy = async () => {
    if (!wallet) {
      return;
    }

    try {
      await navigator.clipboard.writeText(wallet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const filteredTransactions = useMemo(() => {
    if (!transactions || !searchTerm.trim()) {
      return transactions;
    }

    let list = [];
    if (transactions.data && Array.isArray(transactions.data)) {
      list = transactions.data;
    } else if (Array.isArray(transactions)) {
      list = transactions;
    } else if (transactions.ops?.[0]?.list && Array.isArray(transactions.ops[0].list)) {
      list = transactions.ops[0].list;
    } else if (transactions.ops?.[0]?.data?.list && Array.isArray(transactions.ops[0].data.list)) {
      list = transactions.ops[0].data.list;
    }

    const filtered = searchInData(list, searchTerm);
    if (transactions.data && Array.isArray(transactions.data)) {
      return { ...transactions, data: filtered };
    }
    if (Array.isArray(transactions)) {
      return filtered;
    }
    return {
      ...transactions,
      ops: [{ ...transactions.ops[0], list: filtered }]
    };
  }, [transactions, searchTerm]);

  return (
    <div className="page wallet-page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Wallet</h1>
          <p>View the wallet address assigned to your trader profile.</p>
        </div>
      </div>

      <div className="page-content wallet-content">
        <PageLoader loading={walletLoading} error={walletError}>
          {wallet ? (
            <div className="wallet-panel">
              <div className="wallet-qr-card">
                <img src={qrUrl} alt="Wallet QR code" />
              </div>

              <div className="wallet-details">
                <div className="page-section-header">
                  <div className="page-section-title">
                    <h2>Wallet Address</h2>
                    <p>Use this address for trader wallet operations.</p>
                  </div>
                </div>

                <div className="wallet-address" title={wallet}>
                  {wallet}
                </div>

                <button className="reload-button wallet-copy-button" type="button" onClick={handleCopy}>
                  {copied ? 'Copied' : 'Copy Address'}
                </button>
              </div>
            </div>
          ) : (
            <div className="page-placeholder">
              <p>No wallet address configured</p>
            </div>
          )}
        </PageLoader>

        <section className="wallet-transactions">
          <div className="page-section-header">
            <div className="page-section-title">
              <h2>Transactions</h2>
              <p>Browse and inspect your wallet transaction history.</p>
            </div>
            <div className="page-section-meta">
              <div className="page-chip">50 transactions per page</div>
              <div className="page-chip">{searchTerm ? 'Filtered ledger' : 'Recent activity'}</div>
            </div>
          </div>

          <SearchBar
            onSearch={handleSearch}
            value={searchTerm}
            resultCount={pagination?.total || 0}
            isUpdating={searchTerm !== debouncedSearchTerm}
            placeholder="Search transactions..."
          />

          <PageLoader loading={transactionsLoading} error={transactionsError}>
            {transactions ? (
              <TransactionsTable
                data={filteredTransactions}
                pagination={pagination}
                onPageChange={setCurrentPage}
              />
            ) : (
              <div className="page-placeholder">
                <p>No transactions received</p>
              </div>
            )}
          </PageLoader>
        </section>
      </div>
    </div>
  );
};

export default Wallet;
